import PricingTiers from "@/components/automacao/PricingTiers";
import { automacaoFaq, automacaoSteps, automacaoTiers } from "@/data/automacaoServices";
import { cn } from "@/lib/utils";

/**
 * Seções compartilhadas entre o hub /automacao-empresarial e as páginas de
 * pilar (`ProductPageTemplate`). Só apresentação: os dados vêm de
 * `automacaoServices.ts` — preço, passos e FAQ têm uma fonte só.
 */

const container = "mx-auto max-w-[1180px] px-8 max-[900px]:px-5";

export interface AutomacaoSectionProps {
  className?: string;
}

/** "Do primeiro contato ao sistema rodando" — os 4 passos da implantação. */
export function AutomacaoStepsSection({ className }: AutomacaoSectionProps) {
  return (
    <section className={cn("border-y border-border bg-card", className)}>
      <div className={cn(container, "py-[66px]")}>
        <h2 className="mb-8 text-[29px] font-bold tracking-[-0.03em]">
          Do primeiro contato ao sistema rodando
        </h2>
        <ol className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-[18px]">
          {automacaoSteps.map((s) => (
            <li key={s.n} className="rounded-2xl border border-border bg-background p-6">
              <div className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-accent text-[13px] font-bold text-primary">
                {s.n}
              </div>
              <div className="mt-4 text-[15px] font-bold tracking-[-0.01em]">{s.title}</div>
              <p className="mt-2 text-[13.5px] leading-[1.6] text-muted-foreground">{s.desc}</p>
              <div className="mt-3 text-[11.5px] font-bold text-muted-foreground">{s.when}</div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export interface AutomacaoPricingSectionProps extends AutomacaoSectionProps {
  /** Linha abaixo do título; default fala da faixa fechada no diagnóstico. */
  subtitle?: string;
}

/** "Investimento" — os tiers de `automacaoTiers`. */
export function AutomacaoPricingSection({
  className,
  subtitle = "Faixa fechada depois do diagnóstico — sem surpresa no meio do projeto.",
}: AutomacaoPricingSectionProps) {
  return (
    <section className={cn("border-y border-border bg-card", className)}>
      <div className={cn(container, "py-[70px]")}>
        <h2 className="text-[29px] font-bold tracking-[-0.03em]">Investimento</h2>
        <p className="mb-8 mt-3 text-[15px] text-muted-foreground">{subtitle}</p>
        <PricingTiers tiers={automacaoTiers} />
      </div>
    </section>
  );
}

/** "Antes de você perguntar" — FAQ de `automacaoFaq`. */
export function AutomacaoFaqSection({ className }: AutomacaoSectionProps) {
  return (
    <section className={cn("mx-auto max-w-[820px] px-8 py-[70px] max-[900px]:px-5", className)}>
      <h2 className="mb-7 text-[29px] font-bold tracking-[-0.03em]">Antes de você perguntar</h2>
      <div className="flex flex-col gap-[11px]">
        {automacaoFaq.map((item) => (
          <div key={item.q} className="rounded-[14px] border border-border bg-card px-[22px] py-5">
            <div className="text-[15px] font-bold tracking-[-0.01em]">{item.q}</div>
            <p className="mt-[9px] text-sm leading-[1.65] text-muted-foreground">{item.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
